import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

/**
 * glTF export.
 *
 * Always exports the `content` group, never the whole scene, so the grid helper
 * and light rig don't end up in the file you open in Blender.
 */

/** @returns {Promise<ArrayBuffer|object>} .glb ArrayBuffer, or a .gltf JSON object. */
export function exportGLTF(contentGroup, { binary = true } = {}) {
  const exporter = new GLTFExporter();
  return new Promise((resolve, reject) => {
    exporter.parse(
      contentGroup,
      resolve,
      (err) => reject(new Error(`glTF export failed: ${err?.message ?? err}`)),
      { binary, onlyVisible: false },
    );
  });
}

/** Export and hand the browser a download. Used by the toolbar button. */
export async function downloadGLTF(contentGroup, { binary = true, filename } = {}) {
  const output = await exportGLTF(contentGroup, { binary });
  const name = filename ?? `anvil-${timestamp()}.${binary ? 'glb' : 'gltf'}`;

  const blob = binary
    ? new Blob([output], { type: 'model/gltf-binary' })
    : new Blob([JSON.stringify(output, null, 2)], { type: 'model/gltf+json' });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  // Revoke on the next tick — revoking synchronously can cancel the download
  // in some browsers before it has actually started.
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return { filename: name, byteLength: blob.size };
}

/** Export as base64 so the result can travel back over the bridge to the CLI. */
export async function exportGLTFBase64(contentGroup, { binary = true } = {}) {
  const output = await exportGLTF(contentGroup, { binary });
  const bytes = binary
    ? new Uint8Array(output)
    : new TextEncoder().encode(JSON.stringify(output, null, 2));

  return {
    format: binary ? 'glb' : 'gltf',
    byteLength: bytes.byteLength,
    base64: bytesToBase64(bytes),
  };
}

function timestamp() {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

/**
 * btoa() only takes a binary string, and spreading a large Uint8Array into
 * String.fromCharCode blows the argument limit on real meshes, so chunk it.
 */
function bytesToBase64(bytes) {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
